import { map } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { Component, Input, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import {
    Company,
    CompanyService,
    GraphQlService,
    Location,
    LocationService,
    Session,
    Hours,
    SessionService,
    Trainer,
    TrainerService,
    Training,
    TrainingService,
} from '../../../_services';
import { Principal } from '../../../shared/auth/principal.service';
import { CommaDotPipe } from '../../_shared/comma-dot.pipe';
import * as moment from 'moment';

@Component({
    selector: 'session-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.style.scss'],
    providers: [TranslatePipe, CommaDotPipe],
})
export class SessionFormComponent implements OnInit {
    @Input() currentSession: Session;
    @Input() type: string;

    error: string;
    success = false;
    invalidForm = false;
    account: any;
    trainings: Training[] = [];
    customTraining = false;
    trainingSelected = false;
    partnerLocationSelected = false;
    zenikaLocationSelected = false;
    companySelected = false;
    roomSelected = false;
    linkedTrainingSelected = false;
    isAdmin = false;
    partnerLocations: Location[];
    zenikaLocations: Location[];
    companies: Company[];
    trainers: Trainer[];
    unavailableRooms: any;
    availabilityTable: any[];
    commercialInfo: string;
    cleanStartDate: any;
    updateAddress = true;
    submitDisabled = false;
    showDateError = false;
    deleteModalRef: any;
    notifyTrainerOnDeletion = true;
    forceGoogleCalendarSyncRunning = false;
    forceGoogleCalendarSyncSuccess = false;
    hoursList: Hours[] = [];
    editedHoursList: Hours[] = [];
    timezones: string[];
    isHoursValid = true;
    editHoursModalRef: any;

    constructor(
        private principal: Principal,
        private trainingService: TrainingService,
        private sessionService: SessionService,
        private locationService: LocationService,
        private companyService: CompanyService,
        private trainerService: TrainerService,
        private graphQlService: GraphQlService,
        private modalService: NgbModal
    ) {}

    openEditHoursModal(modal) {
        this.editHoursModalRef = this.modalService.open(modal);
    }

    closeEditHoursModal() {
        this.editHoursModalRef.close();
    }

    initHours() {
        if (this.currentSession.hours.length > 0) {
            const hours = this.currentSession.hours.sort((hour1, hour2) => {
                return `${hour1.date} ${hour1.startTime}`.localeCompare(
                    `${hour2.date} ${hour2.startTime}`
                );
            });
            this.hoursList = hours.map((value) => ({ ...value }));
            this.editedHoursList = hours.map((value) => ({ ...value }));
        } else {
            this.fetchHours();
        }
    }

    fetchHours() {
        if (!this.currentSession.days || !this.cleanStartDate) {
            return;
        }
        const hours = this.getComputedHours();
        this.hoursList = hours.map((value) => ({ ...value }));
        this.editedHoursList = hours.map((value) => ({ ...value }));
    }

    getComputedHours() {
        if (!this.cleanStartDate) {
            return [];
        }
        let hours: Hours[] = [];
        for (let i = 0; i < this.currentSession.days * 2; i++) {
            let iterationDate = new Date(this.cleanStartDate);
            iterationDate.setDate(iterationDate.getDate() + i / 2);
            hours.push({
                date: iterationDate.toISOString().split('T')[0],
                startTime: i % 2 === 0 ? '09:00' : '13:30',
                endTime: i % 2 === 0 ? '12:30' : '17:00',
            });
        }
        this.isHoursValid = true;
        return hours;
    }

    addHour() {
        this.editedHoursList.push({
            date: '',
            startTime: '',
            endTime: '',
        });
        this.isHoursValid = false;
    }

    resetToDefaultHours() {
        this.editedHoursList = this.getComputedHours();
        this.isHoursValid = true;
    }

    removeHours(index: number) {
        this.editedHoursList.splice(index, 1);
        if (this.editedHoursList.length === 0) {
            this.isHoursValid = false;
        } else {
            this.checkHoursFields();
        }
    }

    cancelHoursModifications() {
        this.editedHoursList = this.hoursList.map((value) => ({ ...value }));
        this.isHoursValid = true;
        this.closeEditHoursModal();
    }

    applyHoursModification() {
        this.hoursList = this.editedHoursList.map((value) => ({ ...value }));
        this.closeEditHoursModal();
    }

    checkHoursFields() {
        if (this.editedHoursList.length === 0) {
            return;
        }
        this.isHoursValid = this.editedHoursList.every(
            ({ date, startTime, endTime }) =>
                Boolean(date && startTime && endTime)
        );
    }

    get priceNoCents() {
        if (isNaN(this.currentSession.billing.price)) {
            this.currentSession.billing.price = 0;
        }
        return this.currentSession.billing.price / 100;
    }

    set priceNoCents(price: number | null) {
        this.currentSession.billing.price =
            price === null
                ? 0
                : Number.parseInt(price.toFixed(2).replace('.', ''));
    }

    ngOnInit() {
        this.findAllZenikaLocations();
        this.initAccount();
        if (this.type === 'update') {
            this.cleanStartDate = moment(this.currentSession.startDate).format(
                'YYYY-MM-DD'
            );
        }
        if (
            this.currentSession.trainingSessionExtended === null ||
            typeof this.currentSession.trainingSessionExtended === 'undefined'
        ) {
            this.currentSession.trainingSessionExtended = {
                subject: '',
                address: '',
                linkedTraining: null,
            };
        }
        if (
            this.currentSession.trainingSessionExtended.address &&
            this.currentSession.trainingSessionExtended.address !== ''
        ) {
            // We don't change the address in the initialisation if it already exists previously
            this.updateAddress = false;
        }
        if (!this.currentSession.additionalTrainers) {
            this.currentSession.additionalTrainers = [];
        }
        if (!this.currentSession.billing.priceCurrency) {
            this.currentSession.billing.priceCurrency = 'EURO';
        }

        if (!this.currentSession.fees) {
            this.currentSession.fees = {
                educFees: 0,
                educFeesCurrency: 'EURO',
                amountsPaid: 0,
                amountsPaidCurrency: 'EURO',
                lunches: 0,
                lunchesCurrency: 'EURO',
                roomRental: 0,
                roomRentalCurrency: 'EURO',
            };
        }

        this.fetchTimeZones();
        this.initHours();
    }

    fetchTimeZones() {
        return this.graphQlService
            .post({
                query: `
                    query {
                      timezone_names: training_session_allowed_timezone {
                        name
                      }
                    }
            `,
                variables: {},
            })
            .subscribe(({ timezone_names }) => {
                this.timezones = timezone_names.map(({ name }) => name);
            });
    }

    updateSynchronizeSignatureValue() {
        this.currentSession.synchronizeSignature =
            this.currentSession.timezone === 'Europe/Paris';
    }

    fetchTrainings(keyword: string, language = 'fr') {
        return this.graphQlService
            .post({
                query: `
                    query($keyword: String!, $language: String!) {
                      trainings: training(
                        where: {
                          _and: [
                            { _or: [{ active: { _eq: true } }, { published: { _eq: true } }] }
                            { deleted: { _eq: false } }
                            { title: { _ilike: $keyword } }
                          ]
                        }
                        order_by: [{ title: asc_nulls_first }]
                      ) {
                        id
                        title
                        reference
                        days
                        metadata(where: { language: { _eq: $language } }) {
                          materialReady: material_ready
                        }
                      }
                    }
                `,
                variables: {
                    keyword: `%${keyword}%`,
                    language,
                },
            })
            .pipe(
                map(({ trainings }) =>
                    trainings.map((training) => ({
                        id: training.id,
                        title: training.title,
                        reference: training.reference,
                        days: training.days,
                        materialReady: training.metadata[0].materialReady,
                    }))
                )
            );
    }

    findAllZenikaLocations() {
        this.locationService
            .findAllZenika()
            .subscribe((locations) => (this.zenikaLocations = locations));
    }

    searchPartnersLocations(keyword: string) {
        return this.locationService.searchPartners(keyword);
    }

    fetchCompanies(keyword: string) {
        return this.companyService.search(keyword);
    }

    fetchTrainers(keyword: string) {
        return this.trainerService.search(keyword);
    }

    initAccount() {
        this.principal.identity().then((account) => {
            this.account = account;
            this.commercialInfo = account.firstName + ' ' + account.lastName;
            if (this.account.authorities.includes('ROLE_ADMIN')) {
                this.isAdmin = true;
            }
        });
    }

    myListFormatter(data: any): string {
        let html = '';
        html += data.title ? `<span><strong>${data.title}</strong></span>` : '';
        html += data.id ? `<span> (${data.id})</span>` : '';

        return html;
    }

    myLocationListFormatter(data: any): string {
        let html = '';
        if (data.unavailable) {
            html += `<i class="fa fa-exclamation-triangle" aria-hidden="true"></i> `;
        }
        html += data.name ? `<span><strong>${data.name}</strong></span>` : '';
        html += data.id ? `<span> (#${data.id})</span>` : '';

        return html;
    }

    myCompanyListFormatter(data: any): string {
        let html = '';
        html += data.name ? `<span><strong>${data.name}</strong></span>` : '';
        html += data.city ? `<span> (${data.city})</span>` : '';

        return html;
    }

    myTrainerListFormatter(data: any): string {
        let html = '';
        html += data.fullName
            ? `<span><strong>${data.fullName}</strong></span>`
            : '';
        html += data.id ? `<span> (#${data.id})</span>` : '';
        return html;
    }

    testIfRoomIsAvailable() {
        if (
            this.currentSession.location &&
            this.currentSession.location.rooms &&
            this.unavailableRooms
        ) {
            this.currentSession.location.rooms =
                this.currentSession.location.rooms.filter(
                    (room) => [13395, 13394].indexOf(room.id) === -1
                );

            this.resetRoomAvailability();
            let startDay = moment(this.cleanStartDate).weekday();
            for (let room1 of this.currentSession.location.rooms) {
                for (let day = 0; day < this.currentSession.days; day++) {
                    switch (startDay + day) {
                        case 0:
                            for (let room2 of this.unavailableRooms.monday) {
                                if (room2.name === room1.name) {
                                    room1.unavailable = true;
                                }
                            }
                            break;
                        case 1:
                            for (let room2 of this.unavailableRooms.tuesday) {
                                if (room2.name === room1.name) {
                                    room1.unavailable = true;
                                }
                            }
                            break;
                        case 2:
                            for (let room2 of this.unavailableRooms.wednesday) {
                                if (room2.name === room1.name) {
                                    room1.unavailable = true;
                                }
                            }
                            break;
                        case 3:
                            for (let room2 of this.unavailableRooms.thursday) {
                                if (room2.name === room1.name) {
                                    room1.unavailable = true;
                                }
                            }
                            break;
                        case 4:
                            for (let room2 of this.unavailableRooms.friday) {
                                if (room2.name === room1.name) {
                                    room1.unavailable = true;
                                }
                            }
                            break;
                    }
                }
            }
        }
    }

    resetRoomAvailability() {
        for (let i = 0; i < this.currentSession.location.rooms.length; i++) {
            this.currentSession.location.rooms[i].unavailable = false;
        }
    }

    findUnavailableRoomsForWeek() {
        return new Promise((resolve, reject) => {
            let date = moment(this.cleanStartDate).startOf('week');
            this.locationService
                .findUnavailableRoomsForWeek(date)
                .subscribe((rooms) => {
                    this.unavailableRooms = rooms;

                    const momentWeekEndDate = moment(
                        this.unavailableRooms.weekStartDate
                    );
                    this.unavailableRooms.weekEndDate = moment(
                        momentWeekEndDate
                    ).add(4, 'days');
                    this.unavailableRooms.weekStartDateW = ['', '', '', '', ''];
                    for (let i = 0; i <= 4; i++) {
                        this.unavailableRooms.weekStartDateW[i] = moment(
                            momentWeekEndDate
                        )
                            .add(i, 'days')
                            .date();
                    }

                    this.testIfRoomIsAvailable();
                    this.createAvailabilityRoomTable();
                    resolve();
                });
        });
    }

    createAvailabilityRoomTable() {
        if (this.currentSession.location && this.unavailableRooms) {
            let availabilityMap: Map<string, any> = new Map();
            for (let room of this.currentSession.location.rooms) {
                availabilityMap.set(room.name, Object.assign({}, room));
            }
            for (let room of this.currentSession.location.rooms) {
                for (let room2 of this.unavailableRooms.monday) {
                    if (room.name === room2.name) {
                        availabilityMap.get(room2.name).monday = true;
                    }
                }
                for (let room2 of this.unavailableRooms.tuesday) {
                    if (room.name === room2.name) {
                        availabilityMap.get(room2.name).tuesday = true;
                    }
                }
                for (let room2 of this.unavailableRooms.wednesday) {
                    if (room.name === room2.name) {
                        availabilityMap.get(room2.name).wednesday = true;
                    }
                }
                for (let room2 of this.unavailableRooms.thursday) {
                    if (room.name === room2.name) {
                        availabilityMap.get(room2.name).thursday = true;
                    }
                }
                for (let room2 of this.unavailableRooms.friday) {
                    if (room.name === room2.name) {
                        availabilityMap.get(room2.name).friday = true;
                    }
                }
            }
            this.availabilityTable = [];
            for (let value of Array.from(availabilityMap.values())) {
                if (value.id != 5210911) {
                    this.availabilityTable.push(value);
                }
            }
        }
    }

    zenikaLocationChanged() {
        this.companySelected = false;
        this.partnerLocationSelected = false;
        if (this.updateAddress) {
            this.setSessionExtendedAddress(this.currentSession.location);
        } else {
            this.updateAddress = true;
        }
        if (this.roomSelected) {
            this.roomSelected = false;
            this.currentSession.room = null;
        }
        if (typeof this.unavailableRooms === 'undefined') {
            this.findUnavailableRoomsForWeek().then(() => {
                this.testIfRoomIsAvailable();
                this.createAvailabilityRoomTable();
                this.zenikaLocationSelected = true;
            });
        } else {
            this.testIfRoomIsAvailable();
            this.createAvailabilityRoomTable();
            this.zenikaLocationSelected = true;
        }
    }

    partnerLocationChanged() {
        this.companySelected = false;
        this.zenikaLocationSelected = false;
        if (this.updateAddress) {
            this.setSessionExtendedAddress(this.currentSession.location);
        } else {
            this.updateAddress = true;
        }
        this.partnerLocationSelected = true;
    }

    setSessionExtendedAddress(ojectWithAddress) {
        this.currentSession.trainingSessionExtended.address =
            ojectWithAddress.address1 ? ojectWithAddress.address1 + ' ' : '';
        this.currentSession.trainingSessionExtended.address +=
            ojectWithAddress.address2 ? ojectWithAddress.address2 + ' ' : '';
        this.currentSession.trainingSessionExtended.address +=
            ojectWithAddress.zipCode ? ojectWithAddress.zipCode + ' ' : '';
        this.currentSession.trainingSessionExtended.address +=
            ojectWithAddress.city ? ojectWithAddress.city + ' ' : '';
        this.currentSession.trainingSessionExtended.address +=
            ojectWithAddress.countryCode ? ojectWithAddress.countryCode : '';
    }

    isSuccessful(id?: number) {
        this.success = true;
        window.setTimeout(() => {
            window.scrollTo(0, document.body.scrollHeight);
        }, 0);
        window.setTimeout(() => {
            this.success = false;
            if (typeof id !== 'undefined') {
                window.location.href = '#/session/' + id;
            } else {
                window.location.href = '#/sessions';
            }
            this.submitDisabled = false;
        }, 5);
    }

    onTrainingSelected(training) {
        if (this.type === 'create') {
            this.currentSession.days = training.days;
            this.fetchHours();
        }
        this.trainingSelected = training.title !== '';
        if (training.reference) {
            this.customTraining =
                training.reference.indexOf('Formation sur mesure') >= 0 ||
                training.reference.indexOf('SPEED_TRAINING') >= 0;
        }
    }

    onLinkedTrainingSelected(linkedTraining) {
        this.linkedTrainingSelected = linkedTraining.title !== '';
    }

    focusOnTrainingAutocomplete() {
        this.trainingSelected = false;
        this.currentSession.training = null;
    }

    focusOnPartnerLocationAutocomplete() {
        this.partnerLocationSelected = false;
        this.currentSession.location = null;
    }

    focusOnZenikaLocationAutocomplete() {
        this.zenikaLocationSelected = false;
        this.currentSession.location = null;
    }

    focusOnRoomAutocomplete() {
        this.roomSelected = false;
        this.currentSession.room = null;
    }

    focusOnCompanyAutocomplete() {
        this.companySelected = false;
        this.currentSession.company = null;
    }

    focusOnTrainerAutocomplete() {
        this.currentSession.trainer = null;
    }

    focusOnAdditionalTrainersAutocomplete(
        additionalTrainerInInput: Trainer | ''
    ) {
        if (!additionalTrainerInInput) {
            return;
        }
        if (
            !this.currentSession.additionalTrainers.includes(
                additionalTrainerInInput
            )
        ) {
            this.currentSession.additionalTrainers.push(
                additionalTrainerInInput
            );
        }
    }

    focusOnLinkedTrainingAutocomplete() {
        this.linkedTrainingSelected = false;
        this.currentSession.trainingSessionExtended.linkedTraining = null;
    }

    removeAdditionalTrainer(index) {
        this.currentSession.additionalTrainers.splice(index, 1);
    }

    validate(event) {
        if (this.trainingSelected) {
            event.preventDefault();
            this.currentSession.training.deleted = false;
            this.currentSession.hours = this.hoursList;
            this.currentSession.startDate = moment(
                this.currentSession.startDate
            ).toDate();
            let sendingSession: any = Object.assign({}, this.currentSession);

            const { fees } = sendingSession;

            if (
                fees.amountsPaid === null ||
                fees.educFees === null ||
                fees.lunches === null ||
                fees.roomRental === null
            ) {
                this.invalidForm = true;
                return;
            } else {
                this.invalidForm = false;
            }

            sendingSession.startDate = moment(this.cleanStartDate).format(
                'YYYY-MM-DD'
            );
            if (this.type === 'create') {
                this.submitDisabled = true;
                this.sessionService
                    .create(
                        sendingSession,
                        this.commercialInfo,
                        this.availabilityTable
                    )
                    .subscribe(
                        (response) => {
                            if (response !== null) {
                                this.isSuccessful(response.id);
                            }
                        },
                        () => {
                            this.submitDisabled = false;
                        }
                    );
            } else {
                this.submitDisabled = true;
                this.sessionService.update(sendingSession).subscribe(
                    (response) => {
                        if (response !== null) {
                            this.isSuccessful(response.id);
                        }
                    },
                    () => {
                        this.submitDisabled = false;
                    }
                );
            }
        }
    }

    onCompanyChanged(event) {
        this.companySelected = true;
        if (this.updateAddress) {
            this.setSessionExtendedAddress(this.currentSession.company);
        } else {
            this.updateAddress = true;
        }
    }

    onLocationTypeChanged() {
        this.currentSession.trainingSessionExtended.address = null;
        this.currentSession.company = null;
        this.currentSession.location = null;
        this.currentSession.room = null;
        this.companySelected = false;
        this.zenikaLocationSelected = false;
        this.partnerLocationSelected = false;
        this.roomSelected = false;
    }

    showModal(deleteModal) {
        this.deleteModalRef = this.modalService.open(deleteModal);
    }

    closeModal() {
        this.deleteModalRef.close();
    }

    removeSession() {
        this.sessionService
            .remove(this.currentSession, this.notifyTrainerOnDeletion)
            .subscribe(() => {
                this.deleteModalRef.close();
                this.notifyTrainerOnDeletion = true;
                window.location.href = '#/sessions/list';
            });
    }

    updateDays(days) {
        this.hoursList = [];
        this.currentSession.days = days;
        if (this.checkDate()) {
            this.fetchHours();
        }
    }
    updateCurrentSessionType(event) {
        if (event.target.value) {
            this.currentSession.type = event.target.value;
        }
    }

    checkDate() {
        this.showDateError = false;
        if (!this.cleanStartDate || !this.currentSession.days) {
            return;
        }

        const startDate = moment(this.cleanStartDate);
        const startDateWeekIndex = startDate.week();

        const duration = Math.ceil(parseFloat(this.currentSession.days + ''));

        const endDate = moment(startDate).add(duration - 1, 'day');
        const endDateDayOfWeekIndex = endDate.weekday();
        const endDateWeekIndex = endDate.week();

        if (
            endDateDayOfWeekIndex > 4 ||
            startDateWeekIndex !== endDateWeekIndex ||
            duration > 5
        ) {
            this.showDateError = true;
            return false;
        }
        return true;
    }

    async forceGoogleCalendarSync() {
        this.forceGoogleCalendarSyncRunning = true;
        try {
            await this.sessionService
                .forceGoogleCalendarSync(this.currentSession.id)
                .toPromise();
            this.forceGoogleCalendarSyncSuccess = true;
            setTimeout(() => {
                this.forceGoogleCalendarSyncSuccess = false;
            }, 2000);
        } catch (err) {
            console.error(err);
        } finally {
            this.forceGoogleCalendarSyncRunning = false;
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/_screens/session/form/form.component.ts